import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationQueryOptions } from '@/lib/query-options';
import { PlanType, PlanPeriod } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { InvoiceHistory } from './invoice-history';
import { CancelSubscriptionModal } from './cancel-subscription-modal';
import { usePaymentGate } from '@/hooks/usePaymentGate';
import fetchWithAuth from '@/lib/apis';
import { apiEndpoint } from '@/lib/consts';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';

export const BiilingInfo = () => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const queryClient = useQueryClient();

  // Use the active organization instead of organizations[0]
  const organizationData = useAtomValue(organizationDataAtom);

  const orgId = organizationData?.id;
  const { data: organization } = useQuery({
    ...organizationQueryOptions(orgId!),
    enabled: !!orgId
  });

  // Payment gate for upgrade modal
  const { PaymentGate, requirePayment } = usePaymentGate({
    source: 'billing_settings',
    returnUrl: window.location.href
  });

  const handleCancellationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['organizations', orgId] });
  };

  const getExpirationDate = (lastCancelDate: string, planPeriod: PlanPeriod) => {
    const cancelDate = new Date(lastCancelDate);
    if (planPeriod === PlanPeriod.YEARLY) {
      cancelDate.setFullYear(cancelDate.getFullYear() + 1);
    } else if (planPeriod === PlanPeriod.MONTHLY) {
      cancelDate.setMonth(cancelDate.getMonth() + 1);
    }
    return cancelDate;
  };

  const handleUpgrade = () => {
    // Use requirePayment to trigger the modal
    requirePayment(() => {});
  };

  useEffect(() => {
    if (organization && !organization.stripe_customer_id && orgId) {
      fetchWithAuth(`${apiEndpoint}/organizations/${orgId}/billing/create-customer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(response => {
          return response.json();
        })
        .catch(error => console.error('  ** Failed to create customer:', error));
    }
  }, [organization, orgId]);

  if (!organization) {
    return <div>Loading...</div>;
  }

  return (
    <div className='grid grid-cols-2 gap-6'>
      <Card className='rounded-lg border bg-white'>
        <CardContent className='p-6'>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Current Plan</h3>
              <p className="text-gray-900">
                  {organization.plan_type === PlanType.FREE && 'Free Plan'}
                  {organization.plan_type === PlanType.SMB && 'SMB Plan - €499/month'}
                  {organization.plan_type === PlanType.ENTERPRISE && 'Enterprise Plan'}
                  {organization.plan_type === PlanType.CUSTOM && 'Custom Plan'}
                </p>
              </div>
              
              {organization.plan_type === PlanType.FREE && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-900">
                    Upgrade to deploy campaigns to Google Ads
                  </p>
                  <Button onClick={handleUpgrade}>
                    Upgrade to SMB Plan - €499/month
                  </Button>
                </div>
              )}
              
              {organization.plan_type !== PlanType.FREE && (
                <div className="space-y-3">
                  <div className="text-green-600">
                    ✅ {organization.plan_type === PlanType.SMB && 'SMB Plan Active - €499/month'}
                    {organization.plan_type === PlanType.ENTERPRISE && 'Enterprise Plan Active'}
                    {organization.plan_type === PlanType.CUSTOM && 'Custom Plan Active'}
                  </div>
                  
                  {organization.plan_type === PlanType.SMB && !organization.last_cancel_date && (
                    <Button 
                      variant="outline" 
                      onClick={() => setShowCancelModal(true)}
                      className="text-red-600 border-red-600 hover:bg-red-50"
                    >
                      Cancel Subscription
                    </Button>
                  )}

                  {organization.last_cancel_date && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-yellow-800">
                        <strong>Subscription Cancellation Scheduled</strong><br />
                        Your subscription will end on{' '}
                        {getExpirationDate(organization.last_cancel_date, organization.plan_period).toLocaleDateString()}
                        . You'll continue to have access to all SMB features until then.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      
      <InvoiceHistory />
      
      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        organizationId={orgId!}
        onSuccess={handleCancellationSuccess}
      />

      {/* Payment Gate Modal */}
      <PaymentGate />
    </div>
  );
};
